import { Loader } from 'lucide-react'
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'
import { Navigate, Outlet } from 'react-router'
import { z } from 'zod'

import { supabase } from './supabase-client'
import { IS_DEV } from '@/env'

import { paths } from '@/config/path'
import { useAuth, useAuthenticatedUser } from '@/state/session'
import { getCurrencyFromCountryCode } from '@/utils/currency'

export const getUser = () => {
  return supabase.auth.getUser()
}

export const logout = () => {
  return supabase.auth.signOut()
}

export const loginInputSchema = z.object({
  email: z.string().min(1, 'Requis').email('Adresse email invalide'),
  password: z.string().min(5, 'Requis'),
})

export type LoginInput = z.infer<typeof loginInputSchema>
export const loginWithEmailAndPassword = (data: LoginInput) => {
  return supabase.auth.signInWithPassword({
    email: data.email,
    password: data.password,
  })
}

export type TokenImpersonationInput = {
  accessToken: string
  refreshToken?: string
}

export const impersonateWithToken = async ({ accessToken, refreshToken }: TokenImpersonationInput) => {
  if (!IS_DEV) {
    throw new Error('Impersonation is only available in development')
  }

  const { data, error } = await supabase.auth.setSession({
    access_token: accessToken,
    refresh_token: refreshToken ?? '',
  })

  if (error) {
    throw error
  }

  return data
}

export const registerInputSchema = z
  .object({
    email: z.string().email('Veuillez saisir une adresse email valide'),
    display_name: z
      .string()
      .min(2, 'Le nom doit contenir au moins 2 caractères')
      .max(50, 'Le nom doit contenir au plus 50 caractères')
      .regex(
        /^[a-zA-ZÀ-ÖØ-öø-ÿ\s'-]+$/,
        'Le nom ne doit contenir que des lettres, des espaces, des tirets et des apostrophes',
      ),
    profile: z.enum(['practician', 'technician']),
    phoneNumber: z
      .string({ required_error: 'Requis' })
      .refine(isValidPhoneNumber, 'Veuillez saisir un numéro de téléphone valide')
      .transform((value) => parsePhoneNumber(value)?.number.toString()),
    countryCode: z.string().default('FR'),
    password: z.string().min(6, 'Le mot de passe doit contenir au moins 6 caractères').max(255),
    confirmPassword: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Les mots de passe ne correspondent pas',
  })

export type RegisterInput = z.infer<typeof registerInputSchema>
export const registerWithEmailAndPassword = (data: RegisterInput) => {
  return supabase.auth.signUp({
    email: data.email,
    password: data.password,
    options: {
      data: {
        display_name: data.display_name,
        profile: data.profile,
        phone_number: data.phoneNumber,
        country_code: data.countryCode,
        currency: getCurrencyFromCountryCode(data.countryCode as string),
      },
      emailRedirectTo: `${window.location.origin}${paths.auth.login.path}`,
    },
  })
}

export const resetPasswordInputSchema = z.object({
  email: z.string().email('Veuillez saisir une adresse email valide'),
})
export type ResetPasswordInput = z.infer<typeof resetPasswordInputSchema>
export const resetPassword = async (data: ResetPasswordInput) => {
  const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
    redirectTo: `${window.location.origin}${paths.auth['update-password'].path}`,
  })

  if (error) {
    throw error
  }
}

export const updatePasswordInputSchema = z.object({
  password: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
  confirmPassword: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
})
export type UpdatePasswordInput = z.infer<typeof updatePasswordInputSchema>
export const updatePassword = (password: UpdatePasswordInput['password']) => {
  return supabase.auth.updateUser({
    password,
  })
}

export const updateEmailInputSchema = z.object({
  email: z.string().email('Veuillez saisir une adresse email valide'),
})
export type UpdateEmailInput = z.infer<typeof updateEmailInputSchema>
export const updateEmail = async (email: UpdateEmailInput['email']) => {
  const { data, error } = await supabase.auth.updateUser({
    email,
  })

  if (error) {
    throw error
  }

  return data
}

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const authState = useAuth()
  // const location = useLocation()

  if (authState.isLoading) {
    return (
      <div className="flex w-full h-screen justify-center items-center">
        <Loader className="animate-spin mr-2" />
        <span className="font-semibold">Chargement en cours...</span>
      </div>
    )
  }

  if (!authState.isAuthenticated) {
    // return <Navigate to={paths.auth.login.getHref(location.pathname)} replace />
    return <Navigate to={paths.auth.login.getHref()} replace />
  }

  return children
}

export const RoleGuard = ({
  requiredRole,
  redirectPath = '/oups',
}: {
  requiredRole: 'practician' | 'technician'
  redirectPath?: string
}) => {
  const user = useAuthenticatedUser()

  if (!user || user.profile.profile !== requiredRole) {
    return <Navigate to={redirectPath} replace />
  }

  return <Outlet />
}
